import { useField, useFormikContext } from 'formik';
import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { usePermissionAwareDisabled } from '../../../contexts/TeamPermissionContext';
import Button from '../../base/Button';
import Icon, { IconName } from '../../base/Icon';
import Text from '../../base/Text';
import DropdownMenu, { DropdownMenuProps } from '../../DropdownMenu';
import { Div, StyledUtilsProps } from '../../helpers/StyledUtils';

interface SelectOption {
  value: string;
  label: string;
  selected_label?: string;
  icon?: IconName | React.ReactElement;
  description?: string;
  disabled?: boolean;
}

interface Props extends StyledUtilsProps {
  label?: string;
  name: string;
  options: SelectOption[];
  help?: string;
  type?: string;
  placeholder?: string;
  auto_complete?: string;
  minimal?: boolean;
  /** Render the 28px (medium) toggle button instead of the default 32px (large). */
  small?: boolean;
  required?: boolean;
  block?: boolean;
  search?: boolean;
  search_placeholder?: string;
  search_term?: string;
  setSearchTerm?: (value: string) => void;
  search_is_loading?: boolean;
  separator?: boolean;
  disabled?: boolean;
  onChange?: (value: string) => void;
  dropdown?: Partial<DropdownMenuProps>;
  sublte_error?: boolean;
  portal?: boolean;
}

export const StyledField = styled(Div)<{ minimal?: boolean; large?: boolean; block?: boolean }>(
  ({ block }) => css`
    width: fit-content;

    ${block &&
    css`
      width: 100%;
    `};

    &:last-of-type {
      margin-bottom: 0;
    }
  `,
);

export const StyledSelect = styled(Div)<{
  error: boolean;
  subtle_error?: boolean;
  has_value: boolean;
}>(
  ({ theme, error, subtle_error = false }) => css`
    position: relative;
    width: 100%;

    button {
      padding-right: ${({ theme }) => theme.spacing(9)};

      &:focus:not(:disabled) {
        box-shadow: ${theme.shadows.input.focused};
      }

      ${error &&
      !subtle_error &&
      css`
        color: ${theme.colors.on.neutral.danger};
        border-color: ${theme.colors.outline.danger};
        background-color: ${theme.colors.surface.container.danger};
        box-shadow: ${theme.shadows.input.error};
      `}

      &:hover {
        background-color: ${theme.colors.surface.base.hover.neutral};
      }
    }

    span {
      pointer-events: none;
    }
  `,
);

const SelectInput: React.FC<Props> = ({
  label,
  name,
  auto_complete,
  required,
  help,
  minimal,
  small,
  options,
  block = false,
  search = false,
  search_placeholder,
  search_term,
  setSearchTerm,
  search_is_loading,
  dropdown = {},
  disabled: _disabled,
  placeholder,
  onChange,
  sublte_error,
  portal,
  ...props
}) => {
  const disabled = usePermissionAwareDisabled(_disabled);
  const [field, meta, { setValue, setTouched }] = useField({
    name,
  });

  let error = meta.error;
  const touched = meta.touched;

  // Empty value don't get validated by default by Formik for Select. Workarround..
  if (!error && required && !field.value) {
    error = 'Required';
  }

  const { submitCount } = useFormikContext();
  const has_error = (touched || submitCount > 0) && !!error;
  const select_ref = useRef<HTMLButtonElement>(null);
  const search_ref = useRef<HTMLInputElement>(null);
  // Cache the selected option so it persists even if the search term changes and the option is no longer in the options array
  const [cached_selected_option, setCachedSelectedOption] = useState(() =>
    field.value ? options.find((o) => o.value === field.value) : undefined,
  );

  useEffect(() => {
    if (field.value) {
      const found = options.find((o) => o.value === field.value);
      if (found) {
        setCachedSelectedOption(found);
      }
    } else {
      setCachedSelectedOption(undefined);
    }
    // Only update when field.value or options change
  }, [field.value, options]);

  const selected_option = field.value
    ? options.find((o) => o.value === field.value) || cached_selected_option
    : undefined;

  return (
    <Div m={{ bottom: 4 }} {...props}>
      <StyledField minimal={minimal} block={block}>
        {label && (
          <Div flex={{ justify: 'space-between', align: 'center' }}>
            <label htmlFor={name}>
              <Text size="xs" semi muted>
                {label}
                {required && (
                  <Text danger as="span">
                    *
                  </Text>
                )}
              </Text>
            </label>
          </Div>
        )}
        <DropdownMenu
          p={0}
          parent_width
          search_ref={search_ref}
          search={search}
          portal={portal}
          search_placeholder={search_placeholder}
          search_term={search_term}
          setSearchTerm={setSearchTerm}
          search_is_loading={search_is_loading}
          placement={'bottom'}
          onToggle={(opened) => {
            if (!opened && search) {
              // shouldValidate false: on option click this fires before setValue, so its
              // validation would race against the fresh one; the empty+required case is
              // already covered by the local "Required" fallback above.
              setTouched(true, false);
            }
          }}
          renderToggle={(opened, toggle) => (
            <StyledSelect error={has_error} subtle_error={sublte_error} has_value={!!field.value}>
              <Button
                value={field.value || ''}
                autoComplete={auto_complete}
                required={required}
                disabled={disabled}
                ref={select_ref}
                outline
                large={!small}
                w={
                  dropdown.parent_width &&
                  typeof dropdown.parent_width === 'object' &&
                  dropdown.parent_width.min
                    ? { px: dropdown.parent_width.min }
                    : 100
                }
                onMouseDown={() => {
                  if (disabled) return;
                  toggle(!opened);
                  setTimeout(() => search_ref.current?.focus(), 0);
                }}>
                {!!selected_option &&
                  selected_option?.icon &&
                  (typeof selected_option.icon === 'string' ? (
                    <Icon icon={selected_option.icon as IconName} m={{ r: 2 }} muted />
                  ) : (
                    <Div m={{ r: 2 }} flex={{ align: 'center' }}>
                      {selected_option.icon}
                    </Div>
                  ))}
                <Text
                  w={100}
                  align="left"
                  ellipsis
                  muted={!field.value && !has_error}
                  danger={has_error && !sublte_error}>
                  {(selected_option && (selected_option.selected_label || selected_option.label)) ||
                    placeholder ||
                    'Select one...'}
                </Text>
              </Button>

              <Icon
                icon="expand_all"
                muted={disabled || (!field.value && !has_error)}
                danger={has_error && !sublte_error}
                right={-7}
                p={{ t: small ? 1.5 : 2 }}
                style={{ position: 'absolute', cursor: 'pointer' }}
              />
            </StyledSelect>
          )}
          {...dropdown}
          options={options.map((option) => ({
            ...option,
            id: option.value,
            onClick: () => {
              setValue(option.value);
              // Skip setTouched's validation: it validates the pre-setValue values and its
              // stale result would overwrite the fresh validation triggered by setValue.
              setTouched(true, false);
              onChange && onChange(option.value);
            },
          }))}
        />
      </StyledField>
      {has_error && (
        <Text m={{ t: 1, b: 0 }} size="s" as="p" danger>
          <Icon icon="info" left={1} />
          {error}
        </Text>
      )}
      {help && !has_error && (
        <Text m={{ t: 1, b: 0 }} size="s" as="p" muted>
          <Icon icon="info" left={1} />
          {help}
        </Text>
      )}
    </Div>
  );
};

export default SelectInput;
